import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dialog from '../dialog/dialog';
import PanelCard from '../panel-card/panel-card';
import './custom-texteditor.scss';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  LeftAlignIcon,
  CenterAlignIcon,
  RightAlignIcon,
  JustifiedAlignIcon,
  // LinkOptionIcon,
  ImageOptionIcon,
  BulletListIcon,
  NumberListIcon,
  TableIconIcon,
} from './wrappedIcons';

import { RejectIcon, AcceptIcon, SendIcon, AttachIcon } from '../icons/icons';
import FileItem from '../file-item/file-item';
import { FormattIcon } from '../icons/editor';

function CustomEditor({
  getEditorValue,
  onSendClick,
  onAttachClick,
  chatFiles,
  onFileDiscardClick,
  enableBullateList,
  enableNumberList,
  enableTable,
  enableImageUpload,
  disabled,
  disabledSend,
  visibleOptions,
  charLimit,
  width,
  label,
  id,
  required,
  placeholder,
  disabledOptions,
  handleNoteData,
  className,
  disabledAttach,
  disableFontSize,
  disableUnderline,
  disableAlignment,
  movetoBottom,
  defaultvalue,
}) {
  const editorRef = useRef(null);
  const [showDialog, setShowDialog] = useState(false);
  const [formatVisibility, setFormatVisibility] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [selectionRange, setSelectionRange] = useState(null);
  const [charCount, setCharCount] = useState(0);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [currentButton, setCurrentButton] = useState('');
  const [visibleEditor, setVisibleEditor] = useState(true);
  // const [edotorText, setEditorText] = useState('');

  const applyStyle = (style, value) => {
    document.execCommand(style, false, value);
    editorRef.current.focus();
    const content = editorRef.current.innerHTML;
    getEditorValue(content);

    setIsBold(document.queryCommandState('bold'));
    setIsItalic(document.queryCommandState('italic'));
    setIsUnderline(document.queryCommandState('underline'));
  };

  const updateFormattingState = () => {
    setIsBold(document.queryCommandState('bold'));
    setIsItalic(document.queryCommandState('italic'));
    setIsUnderline(document.queryCommandState('underline'));
  };

  useEffect(() => {
    if (editorRef.current && document.activeElement !== editorRef.current) {
      editorRef.current.innerHTML = defaultvalue || '';
    }
  }, [defaultvalue]);

  useEffect(() => {
    const editor = editorRef.current;

    const handlePasteCut = (event) => {
      event.preventDefault();
      const text = event.clipboardData.getData('text');
      const currentLength = editor.innerText.replace(/\s/g, '').length;
      let allowedText = text;

      if (charLimit && currentLength + text.length > charLimit) {
        allowedText = text.slice(0, charLimit - currentLength);
      }

      document.execCommand('insertText', false, allowedText);

      const newCharCount = editor.innerText.replace(/\s/g, '').length;
      setCharCount(newCharCount);
      getEditorValue(newCharCount > 0 ? editor.innerHTML : '');
      handleNoteData(newCharCount > 0 ? editor.innerHTML : '');
    };

    editor.addEventListener('paste', handlePasteCut);
    editor.addEventListener('cut', handlePasteCut);

    return () => {
      editor.removeEventListener('paste', handlePasteCut);
      editor.removeEventListener('cut', handlePasteCut);
    };
  }, [charLimit]);

  const handleKeyPress = (event) => {
    const editorTextContent = editorRef.current.innerText.replace(/\s/g, '');

    if ((event.ctrlKey || event.metaKey) && event.key === 'a') return;

    updateFormattingState();

    if (
      charLimit &&
      editorTextContent.length >= charLimit &&
      event.key !== 'Backspace'
    ) {
      event.preventDefault();
    } else {
      setTimeout(() => {
        const newCharCount = editorRef?.current?.innerText?.replace(
          /\s/g,
          ''
        ).length;
        setCharCount(newCharCount);
        getEditorValue(newCharCount > 0 ? editorRef.current.innerHTML : '');
        handleNoteData(newCharCount > 0 ? editorRef.current.innerHTML : '');
      }, 0);
    }

    if (event.key === 'Enter' && event.target.tagName === 'A') {
      window.open(event.target.href, '_blank');
    }

    if (editorRef.current.innerHTML === '<br>') {
      editorRef.current.innerHTML = '';
    }
    if (event.key === 'Backspace' && editorTextContent.length === 0) {
      setVisibleEditor(false);
      setTimeout(() => {
        setVisibleEditor(true);
        editorRef?.current?.focus();
      }, 100);
    }
  };

  // useEffect(() => {
  //   setEditorText(editorRef.current.innerHTML);

  //   const updateState = () => updateFormattingState();
  //   document.addEventListener('selectionchange', updateState);
  //   return () => document.removeEventListener('selectionchange', updateState);
  // }, []);

  const handleChangeFontSize = (e) => {
    const fontSize = e.target.value;
    document.execCommand('fontSize', false, '7');
    const fontElements = document.getElementsByTagName('font');
    for (let i = 0; i < fontElements.length; i++) {
      if (fontElements[i].size === '7') {
        fontElements[i].removeAttribute('size');
        fontElements[i].style.fontSize = `${fontSize}px`;
      }
    }
    editorRef.current.focus();
  };

  const handleAlignmentChange = (alignment) => {
    document.execCommand(`justify${alignment}`, false, null);
    editorRef.current.focus();
    setCurrentButton(`justify${alignment}`);
  };

  const handleInsertImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = document.createElement('img');
        img.src = event.target.result;
        editorRef.current.appendChild(img);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClick = (event) => {
    if (event.target.tagName === 'A') {
      window.open(event.target.href, '_blank');
    }
  };

  // const handleInsertLink = () => {
  //   const selection = window.getSelection();
  //   if (selection && selection.rangeCount > 0) {
  //     setSelectionRange(selection.getRangeAt(0));
  //   } else {
  //     setSelectionRange(null);
  //   }
  //   setShowDialog(true);
  // };

  const handleInsertBulletList = () => applyStyle('insertUnorderedList', null);
  const handleInsertNumberList = () => applyStyle('insertOrderedList', null);

  const handleDialogAccept = () => {
    if (!urlInput.trim()) {
      setShowDialog(false);
      return;
    }

    const selection = window.getSelection();
    if (selectionRange) {
      const link = document.createElement('a');
      link.href = urlInput;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.appendChild(selectionRange.extractContents());
      selectionRange.insertNode(link);

      const newRange = document.createRange();
      newRange.setStartAfter(link);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
      setSelectionRange(null);
    } else {
      const link = document.createElement('a');
      link.href = urlInput;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = urlInput;
      editorRef.current.appendChild(link);
    }

    setShowDialog(false);
    setUrlInput('');
  };

  const handleSubmitMessage = () => {
    const editorContent = `${editorRef.current.innerHTML} `;
    getEditorValue(editorContent);
    onSendClick();
    editorRef.current.innerHTML = '';
    setCharCount(0);
    setIsBold(false);
    setIsItalic(false);
    setIsUnderline(false);
  };

  return (
    visibleEditor && (
      <div className="custom-editor-container">
        {label && (
          <label htmlFor={id}>
            {label} {required && <span className="required">*</span>}
          </label>
        )}

        <div
          className={`custom-editor${
            chatFiles.length !== 0 || formatVisibility || visibleOptions
              ? ' larged'
              : ''
          }${disabled ? ' disabled-box' : ''}`}
          style={{ width }}
        >
          {(formatVisibility || visibleOptions) && (
            <div className="toolbar-wrapper top">
              <div className="toolbar">
                {!movetoBottom && (
                  <div className="inline-options">
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn${isBold ? ' active' : ''}`}
                        onClick={() => applyStyle('bold', null)}
                      >
                        <BoldIcon />
                      </button>
                    </div>
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn${isItalic ? ' active' : ''}`}
                        onClick={() => applyStyle('italic', null)}
                      >
                        <ItalicIcon />
                      </button>
                    </div>
                    {!disableUnderline && (
                      <div className="option-wrapper">
                        <button
                          type="button"
                          className={`toolbar-btn${
                            isUnderline ? ' active' : ''
                          }`}
                          onClick={() => applyStyle('underline', null)}
                        >
                          <UnderlineIcon />
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {!disableFontSize && (
                  <div className="fontsize-options">
                    <div className="option-wrapper">
                      <select onChange={handleChangeFontSize}>
                        {[10, 12, 14, 16, 18, 20, 24, 28, 32].map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                {!disableAlignment && (
                  <div className="textalign-options">
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn ${
                          currentButton === 'justifyLeft' && 'active'
                        }`}
                        onClick={() => handleAlignmentChange('Left')}
                      >
                        <LeftAlignIcon />
                      </button>
                    </div>
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn ${
                          currentButton === 'justifyCenter' && 'active'
                        }`}
                        onClick={() => handleAlignmentChange('Center')}
                      >
                        <CenterAlignIcon />
                      </button>
                    </div>
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn ${
                          currentButton === 'justifyRight' && 'active'
                        }`}
                        onClick={() => handleAlignmentChange('Right')}
                      >
                        <RightAlignIcon />
                      </button>
                    </div>
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn ${currentButton}`}
                        onClick={() => handleAlignmentChange('Full')}
                      >
                        <JustifiedAlignIcon />
                      </button>
                    </div>
                  </div>
                )}
                <div className="bullet-list">
                  {enableBullateList && (
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className="toolbar-btn"
                        onClick={handleInsertBulletList}
                      >
                        <BulletListIcon />
                      </button>
                    </div>
                  )}
                  {enableNumberList && (
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className="toolbar-btn"
                        onClick={handleInsertNumberList}
                      >
                        <NumberListIcon />
                      </button>
                    </div>
                  )}
                  {enableTable && (
                    <div className="option-wrapper">
                      <button type="button" className="toolbar-btn">
                        <TableIconIcon />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <div
            ref={editorRef}
            className={`editor-content contentEditable${
              chatFiles.length !== 0 ? ' file-added' : ''
            } ${
              className === '' && disabledOptions ? 'h-150' : ''
            } ${className}`}
            contentEditable
            role="textbox"
            tabIndex="0"
            onClick={handleClick}
            onChange={handleKeyPress}
            onKeyUp={handleKeyPress}
            onKeyDown={handleKeyPress}
            placeholder={placeholder}
          />
          {chatFiles.length !== 0 && (
            <div className="file-list">
              {chatFiles.map((doc) => (
                <FileItem
                  key={doc.name}
                  file={doc}
                  onDiscard={onFileDiscardClick}
                />
              ))}
            </div>
          )}
          {!disabledSend && charLimit && (
            <div
              className={`char-count editor ${
                charCount >= charLimit ? 'exceed-limit' : ''
              }`}
            >
              {charCount}/{charLimit}
            </div>
          )}
          {!disabledOptions && (
            <div className="toolbar-wrapper justify-between">
              <div className="toolbar">
                {!movetoBottom ? (
                  <div className="inline-options">
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn ${
                          formatVisibility ? 'active' : ''
                        }`}
                        onClick={() => setFormatVisibility(!formatVisibility)}
                      >
                        <FormattIcon
                          activeColor={formatVisibility ? '#ff1111' : 'none'}
                        />
                      </button>
                    </div>
                    {!disabledAttach && (
                      <div className="option-wrapper">
                        <label className="toolbar-btn">
                          <AttachIcon />
                          <input
                            type="file"
                            className="attachment"
                            onChange={onAttachClick}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                    )}
                    {enableImageUpload && (
                      <div className="option-wrapper">
                        <label className="toolbar-btn">
                          <ImageOptionIcon />
                          <input
                            type="file"
                            className="attachment"
                            onChange={handleInsertImage}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="inline-options">
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn${isBold ? ' active' : ''}`}
                        onClick={() => applyStyle('bold', null)}
                      >
                        <BoldIcon />
                      </button>
                    </div>
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn${isItalic ? ' active' : ''}`}
                        onClick={() => applyStyle('italic', null)}
                      >
                        <ItalicIcon />
                      </button>
                    </div>
                    {!disableUnderline && (
                      <div className="option-wrapper">
                        <button
                          type="button"
                          className={`toolbar-btn${
                            isUnderline ? ' active' : ''
                          }`}
                          onClick={() => applyStyle('underline', null)}
                        >
                          <UnderlineIcon />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="toolbar">
                <div className="inline-options">
                  {!disabledSend ? (
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className="toolbar-btn send"
                        onClick={handleSubmitMessage}
                      >
                        <SendIcon color="#ffffff" />
                      </button>
                    </div>
                  ) : (
                    <div className="option-wrapper">
                      {charLimit && (
                        <div
                          className={`char-count editor ${
                            charCount >= charLimit ? 'exceed-limit' : ''
                          }`}
                        >
                          {charCount}/{charLimit}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <Dialog
            isShowing={showDialog}
            hide={() => setShowDialog(false)}
            placement="center"
          >
            <PanelCard>
              <div>
                <h5>Enter the URL</h5>
                <div className="d-flex align-center">
                  <input
                    className="mr-5"
                    type="text"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                  />
                  <div className="dialog-buttons">
                    <button
                      type="button"
                      style={{ border: 'none', background: 'none' }}
                      onClick={handleDialogAccept}
                    >
                      <AcceptIcon color="#55b938" />
                    </button>
                    <button
                      type="button"
                      style={{ border: 'none', background: 'none' }}
                      onClick={() => setShowDialog(false)}
                    >
                      <RejectIcon color="#f02b1f" />
                    </button>
                  </div>
                </div>
              </div>
            </PanelCard>
          </Dialog>
        </div>
      </div>
    )
  );
}

export default CustomEditor;

CustomEditor.propTypes = {
  getEditorValue: PropTypes.func,
  onSendClick: PropTypes.func,
  onAttachClick: PropTypes.func,
  chatFiles: PropTypes.instanceOf(Array),
  onFileDiscardClick: PropTypes.func,
  enableBullateList: PropTypes.bool,
  enableNumberList: PropTypes.bool,
  enableTable: PropTypes.bool,
  enableImageUpload: PropTypes.bool,
  disabled: PropTypes.bool,
  disabledSend: PropTypes.bool,
  visibleOptions: PropTypes.bool,
  charLimit: PropTypes.number,
  width: PropTypes.string,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  id: PropTypes.string,
  required: PropTypes.bool,
  disabledOptions: PropTypes.bool,
  handleNoteData: PropTypes.func,
  className: PropTypes.string,
  disabledAttach: PropTypes.bool,
  disableFontSize: PropTypes.bool,
  disableUnderline: PropTypes.bool,
  disableAlignment: PropTypes.bool,
  movetoBottom: PropTypes.bool,
  defaultvalue: PropTypes.string,
};

CustomEditor.defaultProps = {
  getEditorValue: () => {},
  onSendClick: () => {},
  onAttachClick: () => {},
  chatFiles: [],
  onFileDiscardClick: () => {},
  enableBullateList: false,
  enableNumberList: false,
  enableTable: false,
  enableImageUpload: false,
  disabled: false,
  disabledSend: false,
  visibleOptions: false,
  charLimit: null,
  width: '',
  placeholder: '',
  label: '',
  id: '',
  required: false,
  disabledOptions: false,
  handleNoteData: () => {},
  className: '',
  disabledAttach: false,
  disableFontSize: false,
  disableUnderline: false,
  disableAlignment: false,
  movetoBottom: false,
  defaultvalue: '',
};
